import React, { useEffect, useState } from "react";
import {
  addLuckyDrawOption,
  createLuckyDraw,
  deleteLuckyDraw,
  deleteLuckyDrawOption,
  fetchLuckyDraw,
  fetchLuckyDraws,
  updateLuckyDraw,
  updateLuckyDrawOption,
} from "../../services/promotionApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const emptyCampaign = {
  title: "",
  description: "",
  type: "SCRATCH",
  imageUrl: "",
  maxAttemptsPerUser: 1,
  unavailableMessage: "Sell a device to unlock another scratch card!",
  couponValidityDays: 30,
  isActive: true,
  displayOrder: 0,
};

const emptyOption = {
  label: "",
  value: "",
  isNeverWin: false,
  weight: 1,
  offerText: "",
  couponCode: "",
  colorHex: "#004CFF",
  displayOrder: 0,
};

const LuckyDraws = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyCampaign);
  const [optionForm, setOptionForm] = useState(emptyOption);
  const [editingId, setEditingId] = useState(null);
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setCampaigns(await fetchLuckyDraws());
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const selectCampaign = async (id) => {
    try {
      setSelected(await fetchLuckyDraw(id));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCampaignSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      type: "SCRATCH",
      maxAttemptsPerUser: Number(form.maxAttemptsPerUser) || 1,
      couponValidityDays: Number(form.couponValidityDays ?? 30),
      displayOrder: Number(form.displayOrder) || 0,
    };
    try {
      if (editingId) await updateLuckyDraw(editingId, payload);
      else await createLuckyDraw(payload);
      setEditingId(null);
      setForm(emptyCampaign);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const payload = {
      ...optionForm,
      weight: Number(optionForm.weight) || 1,
      displayOrder: Number(optionForm.displayOrder) || 0,
    };
    try {
      if (editingOptionId) await updateLuckyDrawOption(editingOptionId, payload);
      else await addLuckyDrawOption(selected.id, payload);
      setEditingOptionId(null);
      setOptionForm(emptyOption);
      await selectCampaign(selected.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <h2>Scratch cards</h2>
      <p className="subtitle">Scratch-card campaigns that award store coupons. Mark options as &quot;Never win&quot; (e.g. 80–100% teasers) so they never get picked.</p>
      {error && <p className="error-msg">{error}</p>}

      <div className="admin-split">
        <div className="admin-card">
          <h3>{editingId ? "Edit campaign" : "Create campaign"}</h3>
          <form className="admin-form" onSubmit={handleCampaignSubmit}>
            <label className="field-label">Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></label>
            <label className="field-label">Description<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></label>
            <label className="field-label">
              Type
              <select value="SCRATCH" disabled>
                <option value="SCRATCH">Scratch card</option>
              </select>
              <span className="field-hint">Spin / match / mystery draw removed — scratch cards only</span>
            </label>
            <label className="field-label">Max attempts per user<input type="number" min={1} value={form.maxAttemptsPerUser} onChange={(e) => setForm({ ...form, maxAttemptsPerUser: e.target.value })} /></label>
            <label className="field-label">Coupon validity (days)<input type="number" min={0} value={form.couponValidityDays ?? 30} onChange={(e) => setForm({ ...form, couponValidityDays: e.target.value })} /><span className="field-hint">0 = no expiry</span></label>
            <label className="field-label">Unavailable message<textarea value={form.unavailableMessage} onChange={(e) => setForm({ ...form, unavailableMessage: e.target.value })} rows={2} /></label>
            <label className="checkbox-field"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            <button type="submit" className="admin-btn">{editingId ? "Update" : "Create"}</button>
          </form>
        </div>

        <div className="admin-card">
          <h3>Campaigns</h3>
          <TableScroll>
            <table className="admin-table">
              <thead><tr><th>Title</th><th>Type</th><th>Attempts</th><th>Coupon days</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className={selected?.id === c.id ? "row-selected" : undefined}>
                    <td>{c.title}</td>
                    <td>{c.type}</td>
                    <td>{c.maxAttemptsPerUser}</td>
                    <td>{c.couponValidityDays ?? 30}</td>
                    <td className={c.isActive ? "badge-active" : "badge-inactive"}>{c.isActive ? "Yes" : "No"}</td>
                    <td className="actions-cell">
                      <button type="button" className="admin-btn secondary" onClick={() => selectCampaign(c.id)}>Options</button>
                      <button type="button" className="admin-btn secondary" onClick={() => { setEditingId(c.id); setForm({ ...emptyCampaign, ...c }); }}>Edit</button>
                      <button type="button" className="admin-btn danger" onClick={async () => { if (window.confirm("Delete?")) { await deleteLuckyDraw(c.id); setSelected(null); load(); } }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </div>
      </div>

      {selected && (
        <div className="admin-card">
          <h3>Options — {selected.title}</h3>
          <form className="admin-filters" onSubmit={handleOptionSubmit}>
            <label className="field-label">Label<input value={optionForm.label} onChange={(e) => setOptionForm({ ...optionForm, label: e.target.value })} required placeholder="40% off" /></label>
            <label className="field-label">Value<input value={optionForm.value} onChange={(e) => setOptionForm({ ...optionForm, value: e.target.value })} required placeholder="40" /></label>
            <label className="field-label">Coupon code<input value={optionForm.couponCode} onChange={(e) => setOptionForm({ ...optionForm, couponCode: e.target.value })} placeholder="SPIN40" /></label>
            <label className="field-label">Offer text<input value={optionForm.offerText} onChange={(e) => setOptionForm({ ...optionForm, offerText: e.target.value })} /></label>
            <label className="field-label">Weight<input type="number" min={1} value={optionForm.weight} onChange={(e) => setOptionForm({ ...optionForm, weight: e.target.value })} /></label>
            <label className="checkbox-field"><input type="checkbox" checked={optionForm.isNeverWin} onChange={(e) => setOptionForm({ ...optionForm, isNeverWin: e.target.checked })} /> Never win</label>
            <button type="submit" className="admin-btn">{editingOptionId ? "Update option" : "Add option"}</button>
          </form>
          <TableScroll>
            <table className="admin-table">
              <thead><tr><th>Label</th><th>Value</th><th>Coupon</th><th>Never win</th><th></th></tr></thead>
              <tbody>
                {(selected.options || []).map((o) => (
                  <tr key={o.id}>
                    <td>{o.label}</td>
                    <td>{o.value}</td>
                    <td>{o.couponCode || "—"}</td>
                    <td>{o.isNeverWin ? "Yes" : "No"}</td>
                    <td>
                      <button type="button" className="admin-btn secondary" onClick={() => { setEditingOptionId(o.id); setOptionForm({ ...emptyOption, ...o }); }}>Edit</button>
                      <button type="button" className="admin-btn danger" onClick={async () => { await deleteLuckyDrawOption(o.id); selectCampaign(selected.id); }}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </div>
      )}
    </div>
  );
};

export default LuckyDraws;
